import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

interface LabelData {
  slipCode: string;
  jobId: string;
  jobName: string;
  client: string;
  poNumber: string;
  itemCode: string;
  materialCode?: string;
  quantity: string;
  location: string;
  slipDate: string;
  shift: string;
  machine: string;
  qc: string;
  operator: string;
  remark?: string;
}

/**
 * Sticker label sent to the floor.
 *
 * Two visually separate bordered blocks:
 *   1. Client-facing rows (Item Code, Job Name, Client Name, Quantity, Location, PO No.)
 *   2. Production/traceability rows (Date & Shift, Job Card No., M/C, QC, Operator)
 *
 * Rows whose value is blank at print time are dropped entirely.
 *
 * We render the label ONCE via html2canvas, then stamp that single
 * image onto N identical PDF pages.  Earlier versions asked
 * html2pdf.js to paginate a multi-copy HTML, but html2pdf slices one
 * tall canvas by pixel offset — 100mm at html2canvas scale × jsPDF's
 * mm-to-px conversion doesn't land on whole pixels, so copy #3 onward
 * drifted.  Rendering one canvas + repeating the same image guarantees
 * every copy is byte-identical.
 *
 * Modes: 'download' saves as PDF; 'print' opens the OS print dialog
 * against a hidden PDF iframe (so a physical label printer picked as
 * the OS default just prints without producing a PDF file to open).
 */
export async function generateStickerLabel(
  data: LabelData,
  copies: number = 1,
  mode: 'download' | 'print' = 'download',
) {
  // Physical label size — 4×4 inch thermal labels, so 100×100mm.
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.top = '-10000px';
  iframe.style.left = '-10000px';
  iframe.style.width = '100mm';
  iframe.style.height = '100mm';
  iframe.style.border = 'none';
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentWindow!.document;

    // 100×100mm label split into fixed vertical bands:
    //   header (12mm) + gap 1mm + client pane (40mm) + gap 2mm + prod pane (39mm)
    // Total = 94mm inside 3mm top/bottom padding.  Explicit mm heights let
    // html2canvas honor the layout — flex:1 collapses through the
    // rasteriser and leaves the bottom half of the label blank.  Left
    // padding is 5mm (vs 3mm on the right) to buy a couple of mm of
    // clearance against the printer's default leftward bias.
    const labelHtml = `
      <div id="label-root" style="box-sizing:border-box; width:100mm; height:100mm; padding:3mm 3mm 3mm 5mm; font-family:'Arial',sans-serif;">
        <div style="width:100%; height:12mm; display:flex; align-items:center; justify-content:center; gap:2mm;">
          <img src="${window.location.origin}/logo.png" alt="" style="height:9mm; width:auto;" />
          <div style="font-size:14pt; font-weight:bold; letter-spacing:0.2px;">VASUDEV PRINT PACK PVT. LTD.</div>
        </div>

        <div style="height:1mm;"></div>

        ${section([
          row('ITEM CODE',     data.itemCode, { bold: true, big: true }),
          row('MATERIAL CODE', data.materialCode ?? '', { bold: true }),
          row('PO NO.',        data.poNumber, { bold: true }),
          row('JOB NAME',      data.jobName,  { bold: true }),
          row('CLIENT NAME',   data.client,   { bold: true }),
          row('QUANTITY',      data.quantity, { bold: true, big: true }),
          row('LOCATION',      data.location),
        ], 40)}

        <div style="height:2mm;"></div>

        ${section([
          row('JC / BATCH NO.',    data.jobId),
          row('DATE & SHIFT',      dateShift(data.slipDate, data.shift)),
          row('MACHINE NAME',      data.machine),
          row('OPERATOR & SORTER', data.operator),
          row('REMARK',            data.remark ?? ''),
          row('QC',                data.qc),
        ], 39)}
      </div>
    `;

    doc.write(`<!DOCTYPE html><html><head>
      <style>
        html, body { margin:0; padding:0; background:white; }
      </style>
    </head><body>${labelHtml}</body></html>`);
    doc.close();

    // Wait for the logo image to load so html2canvas doesn't snapshot
    // a missing image slot.  The rest of the layout is text so it's
    // ready as soon as the doc closes.
    await new Promise<void>((resolve) => {
      const img = doc.querySelector('img');
      if (!img) return resolve();
      if (img.complete && img.naturalWidth > 0) return resolve();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      // Safety timeout so a slow logo never hangs the print button.
      setTimeout(() => resolve(), 2000);
    });

    const root = doc.getElementById('label-root') as HTMLElement;
    if (!root) throw new Error('label-root missing');

    // One canvas, high enough resolution to be crisp on a 203dpi
    // thermal printer.  scale=3 → ~600dpi effective at 100mm.
    const canvas = await html2canvas(root, {
      scale: 3,
      useCORS: true,
      backgroundColor: '#ffffff',
      // Force width/height in device pixels so we don't inherit any
      // viewport quirks from the iframe.
      width: root.offsetWidth,
      height: root.offsetHeight,
      windowWidth: root.offsetWidth,
      windowHeight: root.offsetHeight,
    });
    const imgData = canvas.toDataURL('image/jpeg', 0.98);

    const pdf = new jsPDF({ unit: 'mm', format: [100, 100], orientation: 'portrait' });
    for (let i = 0; i < copies; i++) {
      if (i > 0) pdf.addPage([100, 100], 'portrait');
      pdf.addImage(imgData, 'JPEG', 0, 0, 100, 100, undefined, 'FAST');
    }

    if (mode === 'print') {
      // Load the PDF into a hidden iframe and trigger print from
      // Chrome's PDF viewer — which has no site/URL/date header.
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      printFrame.src = url;
      document.body.appendChild(printFrame);
      await new Promise<void>((resolve) => {
        printFrame.onload = () => {
          try {
            printFrame.contentWindow?.focus();
            printFrame.contentWindow?.print();
          } finally {
            resolve();
          }
        };
      });
      // Leave the PDF frame around long enough for the print dialog to
      // grab it, then clean up.
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(printFrame);
      }, 5000);
    } else {
      pdf.save(`Label-${data.slipCode}.pdf`);
    }
  } finally {
    document.body.removeChild(iframe);
  }
}

interface RowSpec {
  label: string;
  value: string;
  bold?: boolean;
  big?: boolean;
}

/**
 * Bordered section — one per pane so the two panes read as distinct
 * without a header strip.  Rows with an empty value are DROPPED here so
 * the sticker doesn't carry a labelled empty cell, and the border logic
 * still puts the outer frame around whatever is left.
 */
function section(specs: RowSpec[], boxHeightMm: number): string {
  const rows = specs.filter((r) => (r.value ?? '').trim() !== '');
  if (rows.length === 0) {
    return `<div style="height:${boxHeightMm}mm; border:1pt solid #000; box-sizing:border-box;"></div>`;
  }
  const last = rows.length - 1;
  // Divide the pane evenly among visible rows.  Subtract 0.5mm total for
  // the outer 1pt border so rows sum to the available interior height.
  const rowH = (boxHeightMm - 0.5) / rows.length;
  const rowHmm = `${rowH.toFixed(2)}mm`;
  const trs = rows.map((r, i) => renderRow(r, i === last, rowHmm)).join('');
  return `
    <div style="height:${boxHeightMm}mm; border:1pt solid #000; box-sizing:border-box; overflow:hidden;">
      <table style="width:100%; border-collapse:collapse; table-layout:fixed;">
        ${trs}
      </table>
    </div>
  `;
}

function renderRow(r: RowSpec, isLast: boolean, rowHeight: string): string {
  const rowBorder = isLast ? '' : 'border-bottom:0.5pt solid #000;';
  const labStyle = `${rowBorder} border-right:0.5pt solid #000; padding:1mm 2mm; width:16mm; font-size:8pt; font-weight:bold; vertical-align:middle; background:#f2f2f2; word-break:break-word;`;

  // Aggressive font shrink for long values + transform: scaleX() as a
  // paint-time squeeze fallback.  html2canvas honours transforms on
  // inline-block spans reliably; text-overflow:ellipsis guards
  // whatever the transform can't fit.  Kept HTML (not SVG) because
  // html2canvas 1.4.1's SVG rasterisation is unreliable and can drop
  // the whole label.
  const value = r.value ?? '';
  const basePt = r.big ? 12 : 10;

  let fontPt = basePt;
  if (value.length >= 20) fontPt = Math.max(basePt - 1, 9);
  if (value.length >= 28) fontPt = Math.max(basePt - 2, 8);
  if (value.length >= 38) fontPt = 8;

  let scaleX = 1;
  if (value.length >= 24) scaleX = 0.9;
  if (value.length >= 32) scaleX = 0.8;
  if (value.length >= 40) scaleX = 0.7;
  if (value.length >= 50) scaleX = 0.6;
  if (value.length >= 65) scaleX = 0.5;

  const valStyle = [
    rowBorder,
    'padding:1mm 2mm',
    `font-size:${fontPt}pt`,
    r.bold ? 'font-weight:bold' : '',
    'vertical-align:middle',
    'overflow:hidden',
    'line-height:1.1',
  ].filter(Boolean).join(';');

  const inner = scaleX < 1
    ? `<span style="display:inline-block;transform:scaleX(${scaleX});transform-origin:left center;white-space:nowrap;">${esc(value)}</span>`
    : esc(value);

  return `
    <tr style="height:${rowHeight};">
      <td style="${labStyle}">${esc(r.label)}</td>
      <td style="${valStyle}">${inner}</td>
    </tr>
  `;
}

function row(label: string, value: string, opts: { bold?: boolean; big?: boolean } = {}): RowSpec {
  return { label, value, ...opts };
}

/** "DD-MM-YYYY DAY" combined, or whichever half is available. */
function dateShift(date: string, shift: string): string {
  const d = date ? fmt(date) : '';
  return [d, shift].filter(Boolean).join(' ');
}

function fmt(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
